import {
  getEndOfDayWithTimezone,
  getEndOfMonthWithTimezone,
  getEndOfWeekWithTimezone,
  getStartOfDayWithTimezone,
  getStartOfMonthWithTimezone,
  getStartOfWeekWithTimezone,
} from "@redotech/util/date";
import { assertNever } from "@redotech/util/type";
import * as z from "zod";
import { AdvancedFilterInput } from "../outbound-labels/fulfillment-group-summary-input";
import {
  CustomDate,
  DateFilterData,
  DateFilterOperator,
  KnownDateFilterTimeFrame,
  TwoDateTupleSchema,
} from "../views/advanced-filters/date-filter";
import { AdvancedFilterType } from "../views/advanced-filters/generic-advanced-filter-data";

export enum ReportTimeFrame {
  MONTH_TO_DATE = "month_to_date",
  LAST_MONTH = "last_month",
  LAST_30_DAYS = "last_30_days",
  LAST_WEEK = "last_week",
  LAST_DAY = "last_day",
  CUSTOM = "custom_date_range",
}

export function reportTimeFrameToCustomDateRange(
  input: ReportDataInput,
): CustomDate | null {
  const tz = input.timezoneId;

  const now = Temporal.Now.zonedDateTimeISO(tz).toPlainDate();

  const timeFrame = input.reportTimeFrame;
  switch (timeFrame) {
    case ReportTimeFrame.MONTH_TO_DATE:
      return [
        getStartOfMonthWithTimezone(now, tz),
        getEndOfDayWithTimezone(now, tz),
      ];
    case ReportTimeFrame.LAST_MONTH:
      return [
        getStartOfMonthWithTimezone(now.subtract({ months: 1 }), tz),
        getEndOfMonthWithTimezone(now.subtract({ months: 1 }), tz),
      ];
    case ReportTimeFrame.LAST_30_DAYS:
      return [
        getStartOfDayWithTimezone(now.subtract({ days: 30 }), tz),
        getEndOfDayWithTimezone(now, tz),
      ];
    case ReportTimeFrame.LAST_WEEK:
      return [
        getStartOfWeekWithTimezone(now.subtract({ weeks: 1 }), tz),
        getEndOfWeekWithTimezone(now.subtract({ weeks: 1 }), tz),
      ];
    case ReportTimeFrame.LAST_DAY:
      return [
        getStartOfDayWithTimezone(now, tz),
        getEndOfDayWithTimezone(now, tz),
      ];
    case ReportTimeFrame.CUSTOM:
      return input.customDateRange;
    default:
      assertNever(timeFrame);
  }
}

const ReportDataInputSchema_ = z.discriminatedUnion("reportTimeFrame", [
  z.object({
    reportTimeFrame: z.literal(ReportTimeFrame.CUSTOM),
    customDateRange: TwoDateTupleSchema,
    timezoneId: z.string(),
  }),
  z.object({
    reportTimeFrame: z.union([
      z.literal(ReportTimeFrame.MONTH_TO_DATE),
      z.literal(ReportTimeFrame.LAST_MONTH),
      z.literal(ReportTimeFrame.LAST_30_DAYS),
      z.literal(ReportTimeFrame.LAST_WEEK),
      z.literal(ReportTimeFrame.LAST_DAY),
    ]),
    customDateRange: z.undefined().or(z.null()),
    timezoneId: z.string(),
  }),
]);
export const ReportDataInputSchema: z.ZodType<ReportDataInput> =
  ReportDataInputSchema_;

export type ReportDataInput = z.infer<typeof ReportDataInputSchema_>;

const ShippedItemReportDataSchema_ = z.object({
  fulfillmentGroupId: z.string(),
  storeName: z.string(),
  orderName: z.string(),
  orderCreatedAt: z.date(),
  labelPurchasedAt: z.date(),
  itemName: z.string(),
  sku: z.string().nullish(),
  description: z.string().nullish(),
  quantityShipped: z.number(),
  totalSales: z.number(),
  labelCost: z
    .union([z.string(), z.number()])
    .nullish()
    .transform((val) => (val != null ? val.toString() : "")),
});
export const ShippedItemReportDataSchema: z.ZodType<ShippedItemReportData> =
  ShippedItemReportDataSchema_;

export interface ShippedItemReportData
  extends z.infer<typeof ShippedItemReportDataSchema_> {}

export function reportInputToSearchInput(
  input: ReportDataInput,
  name: string,
): AdvancedFilterInput {
  const dateFilter: DateFilterData = {
    type: AdvancedFilterType.DATE,
    operator: DateFilterOperator.WITHIN,
    value: KnownDateFilterTimeFrame.CUSTOM,
    customDate: reportTimeFrameToCustomDateRange(input),
    name,
  };

  return {
    advancedFiltersData: [dateFilter],
    merchantTimezone: input.timezoneId,
  };
}
